import { router } from 'expo-router';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import { Image, Linking, Platform, StyleSheet, View } from 'react-native';

import { Button } from '@/components/Button';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { Heading } from '@/components/Heading';
import { Overline } from '@/components/Overline';
import { Screen } from '@/components/Screen';
import { Text } from '@/components/Text';
import { GiftPreviewGrid } from '@/components/gifting/GiftPreviewGrid';
import { APP_STORE_URL } from '@/constants/gifting';
import { colors, radii, spacing } from '@/constants/theme';
import { campaignCopy } from '@/lib/campaignCopy';
import { giftingRouteFor } from '@/lib/giftingRoute';
import { authStore } from '@/stores/auth.store';
import { giftingStore } from '@/stores/gifting.store';

const collage = require('@/assets/images/gifting/gift-collage.jpg');

/**
 * The campaign page — ONE page, both platforms. It used to be two: this
 * landing and a near-identical intro screen behind it, same headline, same
 * CTA, same product grid. Now the landing is the only door and "Claim my
 * gift" goes straight to phone entry.
 *
 * Live state has exactly one CTA. No app-download button anywhere in the
 * funnel: an install detour mid-signup loses people, and installed apps
 * still open automatically via the universal link — no button involved. The
 * app pitch lives on the secured screen, after the gift is locked in.
 *
 * Dark (empty catalog) keeps the App Store button, since downloading is the
 * only thing anyone can do before launch.
 */
export const GiftingWebLanding = observer(() => {
  const { products, loaded, loadError } = giftingStore;
  // Copy source order: catalog response (works logged-out) → user payload
  // (authed native visitors may never fetch the catalog) → shipped fallback.
  const copy = campaignCopy(giftingStore.campaignCopy ?? authStore.user?.gifting_copy);

  useEffect(() => {
    void giftingStore.ensureLoaded();
  }, []);

  const live = loaded && products.length > 0;
  // Signed-in users skip phone entry — asking someone who's already logged in
  // for their number reads as "you don't know me?". Which screen they land on
  // is the server's call, not ours.
  const claimHref = giftingRouteFor(authStore.isAuthenticated ? authStore.user : null);
  // Signed-in and the server gave this account no next step — campaign closed,
  // or not on the allowlist. Read off the SAME signal the index routes on
  // rather than re-deriving it from gifting_enabled/gift_claim: two sources
  // for one answer is how the redirect loop got in. Tell them here rather than
  // after they've picked a product and typed an address. Unauthenticated
  // visitors still see the catalog — plenty of them are athletes without an
  // account yet.
  const ineligible = authStore.isAuthenticated && claimHref === null;

  return (
    <Screen width="wide">
      <View style={styles.body}>
        <Overline>{`MarketPryce ${copy.name}`}</Overline>
        <Heading size="4xl" style={styles.headline}>
          {live ? copy.headline : 'Something good is coming.'}
        </Heading>
        <Text color="textMuted" style={styles.copy}>
          {live
            ? copy.subhead
            : `The ${copy.name} opens soon. Get the MarketPryce Rewards app so you’re ready.`}
        </Text>

        {ineligible ? (
          <EmptyState
            title="For college athletes"
            body={`${copy.name} gifts are reserved for college student-athletes right now — but there are paid Rewards for everyone.`}
            cta={{ label: 'See ways to earn', onPress: () => router.push('/(tabs)/deals') }}
          />
        ) : loadError ? (
          <ErrorState
            title="Couldn't load the gifts"
            body="Check your connection and try again."
            onRetry={() => void giftingStore.load()}
          />
        ) : live && claimHref ? (
          <Button
            label={copy.cta_label}
            onPress={() => router.push(claimHref)}
            style={styles.cta}
          />
        ) : (
          <>
            <Button
              label="Download on the App Store"
              onPress={() => void Linking.openURL(APP_STORE_URL)}
              style={styles.cta}
            />
            <Text variant="caption" color="textMuted" align="center" style={styles.iosNote}>
              iPhone only for now.
            </Text>
          </>
        )}

        {/* Dark state only: once the real grid renders below, the collage is
            the same products twice. */}
        {!live && (
          <View style={styles.collageFrame}>
            <Image
              source={collage}
              style={styles.collage}
              resizeMode="cover"
              accessibilityLabel="Assorted free products from MarketPryce brands"
            />
          </View>
        )}

        {live && <GiftPreviewGrid products={products} />}
      </View>
    </Screen>
  );
});

const styles = StyleSheet.create({
  body: {
    paddingTop: spacing.lg,
  },
  headline: {
    marginTop: spacing.md,
  },
  copy: {
    marginTop: spacing.base,
  },
  cta: {
    marginTop: spacing.lg,
  },
  iosNote: {
    marginTop: spacing.sm,
  },
  collageFrame: {
    aspectRatio: 16 / 9,
    borderRadius: radii.lg,
    borderWidth: 1,
    borderColor: colors.border,
    overflow: 'hidden',
    marginTop: spacing.xl,
  },
  collage: {
    ...StyleSheet.absoluteFillObject,
    // Native: undefined w/h stops the intrinsic-size ScrollView inflation
    // (sim-verified). Web: the browser renders the img at natural size
    // without explicit dimensions, cropping the grid — needs 100%.
    width: Platform.OS === 'web' ? '100%' : undefined,
    height: Platform.OS === 'web' ? '100%' : undefined,
  },
});
